import type {
  ActiveQuoteLine,
  BillingItem,
  BlockerItem,
  ControlTowerItem,
  DispatchAssignment,
  DemoPersistedState,
  DemoSnapshot,
  FlowState,
  MetricCard,
  MobileTodayItem,
  OverviewModule,
  QuoteLine,
  ScenarioStepId,
  SignalItem,
  SyncItem,
  TechnicianLane,
  UnassignedJob,
  WorkOrder,
} from './types.ts';

export const scenarioFocusOrderId = 'WO-1837';
export const scenarioFocusQuoteId = 'QT-1837';
export const syncConflictReason = 'Conflit sur la signature client: la version terrain est plus récente que la copie serveur.';
export const reportRejectReason = 'Photos PAC prises avant remise en service et mention sonde extérieure insuffisante.';

export const INITIAL_FLOW_STATE: FlowState = {
  quoteConverted: false,
  onSite: false,
  photosCaptured: false,
  voiceCaptured: false,
  signatureCaptured: false,
  reportStatus: 'draft',
  rejectionReason: null,
  syncStatus: 'pending',
  syncConflictReason: null,
  invoiceCreated: false,
};

export const KPI_CARDS: MetricCard[] = [
  { title: "Interventions aujourd'hui", value: '42', detail: '11 en cours · 6 urgentes', tone: 'default' },
  { title: 'Délai de facturation', value: '1,8 j', detail: 'Cible opérationnelle: < 2 jours', tone: 'success' },
  { title: 'Rapports vocaux utilisés', value: '78 %', detail: '24/31 interventions clôturées', tone: 'info' },
  { title: 'Trésorerie à risque', value: '18 750 €', detail: '7 dossiers bloqués côté validation', tone: 'warning' },
];

export const CONTROL_TOWER: ControlTowerItem[] = [
  { label: 'Devis envoyés < 24h', value: '14/16', meta: '87,5 % sur les 7 derniers jours' },
  { label: 'Rapports synchronisés sans erreur', value: '96 %', meta: '2 reprises manuelles ce matin' },
  { label: 'Contrats à renouveler', value: '9', meta: '3 à moins de 15 jours' },
];

export const OVERVIEW_MODULES: OverviewModule[] = [
  {
    title: 'Cockpit direction',
    detail: 'KPIs trésorerie, urgences terrain, goulots de validation et renouvellements à risque.',
  },
  {
    title: 'Tableau de planification',
    detail: 'Charge par technicien, arbitrages de compétence, urgences et file d’interventions non affectées.',
  },
  {
    title: 'Cockpit intervention',
    detail: 'Checklist, preuves terrain, brouillon vocal et déclenchement de facture sur le même dossier.',
  },
  {
    title: 'Compagnon mobile',
    detail: 'Planning, exécution, capture hors ligne et synchronisation visible pour les techniciens.',
  },
];

export const CASH_BLOCKERS: BlockerItem[] = [
  {
    title: 'Clinique Valmy',
    issue: 'Validation pièce PAC non confirmée',
    impact: '2 460 € retardés',
    owner: 'Exploitation',
  },
  {
    title: 'DataHub 17',
    issue: 'Rapport vocal à approuver avant facture',
    impact: '980 € prêts à émettre',
    owner: 'Responsable ops',
  },
  {
    title: 'Montparnasse Plaza',
    issue: 'Signature client manquante sur SAV',
    impact: '640 € en attente',
    owner: 'Technicien',
  },
];

export const TECHNICIAN_LANES: TechnicianLane[] = [
  {
    name: 'Nadia L.',
    zone: 'Paris Sud',
    utilization: '4 interventions · 92 % charge',
    jobs: [
      { time: '08:30', title: 'Révision rooftop CVC', client: 'Bastille Offices', status: 'Sur site' },
      { time: '11:15', title: 'Diagnostic pompe à chaleur', client: 'Clinique Valmy', status: 'A venir' },
      { time: '15:00', title: 'Contrat maintenance trimestriel', client: 'Aster Labs', status: 'A venir' },
    ],
  },
  {
    name: 'Yanis M.',
    zone: '92 Ouest',
    utilization: '5 interventions · 100 % charge',
    jobs: [
      { time: '09:00', title: 'Panne armoire électrique', client: 'DataHub 17', status: 'Urgent' },
      { time: '13:00', title: 'Contrôle borne GTB', client: 'Lafayette Résidences', status: 'A venir' },
      { time: '16:30', title: 'Clôture intervention fuite', client: 'Montparnasse Plaza', status: 'A venir' },
    ],
  },
  {
    name: 'Chloé R.',
    zone: 'Lyon Centre',
    utilization: '3 interventions · 74 % charge',
    jobs: [
      { time: '10:30', title: 'Mise en service chaudière', client: 'Groupe Médian', status: 'En route' },
      { time: '14:15', title: 'Maintenance CTA', client: 'Hôtel Beaux-Arts', status: 'A venir' },
    ],
  },
];

export const UNASSIGNED_JOBS: UnassignedJob[] = [
  { title: 'Inspection tableau secours', sla: 'Avant 12:00', owner: 'Exploitation', reason: 'Compétence HTA requise' },
  { title: 'Renouvellement contrat chaudière', sla: 'Cette semaine', owner: 'Commerce', reason: 'Pièces non confirmées' },
  { title: 'Visite SAV fuite PAC', sla: "Aujourd'hui", owner: 'Planification', reason: 'Technicien indisponible secteur 94' },
];

export const DISPATCH_SIGNALS: SignalItem[] = [
  { label: 'Urgences couvertes', value: '6/6', detail: 'Aucune intervention critique non affectée' },
  { label: 'Temps route compressé', value: '-14 %', detail: 'Regroupement par zone appliqué sur 11 interventions' },
  { label: 'Trésorerie prête après terrain', value: '12,4 k€', detail: 'Interventions facturables si validation avant 17h' },
];

export const QUOTE_SIGNALS: SignalItem[] = [
  { label: 'Marge brute cible', value: '41 %', detail: 'Conforme au plancher service critique' },
  { label: 'SLA envoi devis', value: '< 4h', detail: 'Client demandeur déjà qualifié' },
  { label: 'Option récurrente', value: 'Maintenance PAC', detail: 'Offre annuelle à proposer à la signature' },
  { label: 'Validation humaine', value: 'Requise', detail: 'Aucune ligne IA sans approbation exploitation' },
];

const WORK_ORDERS: WorkOrder[] = [
  {
    id: 'WO-1842',
    title: 'Révision rooftop CVC',
    client: 'Bastille Offices',
    site: '12 rue Sedaine, Paris 11',
    status: 'Sur site',
    window: '08:30 - 10:30',
    owner: 'Nadia L.',
    checklist: [
      { label: 'Contrôle compresseur', done: true },
      { label: 'Inspection filtres', done: true },
      { label: 'Mesure pression gaz', done: false },
      { label: 'Validation sécurité', done: false },
    ],
    proof: ['Avant / après filtres', 'Plaque signalétique', 'Photo CTA'],
    timeline: [
      { at: '08:29', label: 'Arrivée sur site confirmée' },
      { at: '08:41', label: 'Checklist ouverte sur mobile' },
      { at: '09:12', label: 'Rapport vocal brouillon synchronisé' },
    ],
    revenue: '1 240 € HT',
    invoiceStatus: "Prêt aujourd'hui",
    nextAction: 'Valider la pression gaz puis émettre la facture maintenance',
    upsell: 'Proposer visite de performance énergétique sous 30 jours',
    voiceDraft:
      'Compresseur stable. Filtres fortement encrassés puis remplacés. Prévoir contrôle pression gaz sur prochain passage si la consommation reste élevée.',
    invoiceAction: 'Créer la facture depuis le rapport après validation exploitation',
  },
  {
    id: 'WO-1837',
    title: 'Diagnostic pompe à chaleur',
    client: 'Clinique Valmy',
    site: '4 avenue Pasteur, Dijon',
    status: 'Prêt à planifier',
    window: '11:15 - 12:30',
    owner: 'Nadia L.',
    checklist: [
      { label: 'Confirmation panne client', done: true },
      { label: 'Récupération historique incidents', done: true },
      { label: 'Pièce de rechange réservée', done: false },
    ],
    proof: ['Historique photos 2025', 'Ticket SAV précédent'],
    timeline: [
      { at: '07:55', label: 'Devis accepté puis converti automatiquement' },
      { at: '08:10', label: 'Affectation prête, pièce en attente' },
    ],
    revenue: '792 € HT',
    invoiceStatus: 'Après intervention',
    nextAction: 'Confirmer la pièce PAC avant départ technicien',
    upsell: 'Basculer vers contrat maintenance PAC critique',
    voiceDraft: 'Prévoir diagnostic complet PAC avec vérification dégivrage et sonde extérieure.',
    invoiceAction: 'Facture après rapport + validation forfait déplacement',
  },
  {
    id: 'WO-1819',
    title: 'Panne armoire électrique',
    client: 'DataHub 17',
    site: 'Zone industrielle de Nanterre',
    status: 'En validation',
    window: '09:00 - 11:00',
    owner: 'Yanis M.',
    checklist: [
      { label: 'Sécurisation coupure', done: true },
      { label: 'Contrôle relais de puissance', done: true },
      { label: 'Signature client', done: true },
    ],
    proof: ['Photo relais HS', 'Signature client', 'Rapport vocal complet'],
    timeline: [
      { at: '09:02', label: 'Intervention démarrée' },
      { at: '10:11', label: 'Signature capturée' },
      { at: '10:16', label: 'Rapport vocal en attente validation' },
    ],
    revenue: '980 € HT',
    invoiceStatus: 'Validation ops',
    nextAction: 'Approuver le rapport vocal et déclencher la facture',
    upsell: 'Créer devis de sécurisation complémentaire des armoires',
    voiceDraft:
      'Relais de puissance défectueux remplacé. Test charge OK. Recommande une visite complémentaire sur les trois autres armoires du site.',
    invoiceAction: 'Créer facture et proposer devis de sécurisation complémentaire',
  },
];

const QUOTE_BASE_LINES: QuoteLine[] = [
  { id: 'travel', label: 'Déplacement et sécurisation site', qtyLabel: '1', quantity: 1, unitCents: 19000 },
  { id: 'labor', label: "Main d'œuvre diagnostic PAC", qtyLabel: '2 h', quantity: 2, unitCents: 9500 },
  { id: 'cleaning', label: 'Kit nettoyage condenseur', qtyLabel: '1', quantity: 1, unitCents: 12000 },
  { id: 'restart', label: 'Contrôle pression / remise en service', qtyLabel: '1', quantity: 1, unitCents: 16000 },
];

const QUOTE_OPTIONAL_LINE: QuoteLine = {
  id: 'probe-check',
  label: 'Contrôle sonde extérieure',
  qtyLabel: '1',
  quantity: 1,
  unitCents: 11000,
};

export const MOBILE_TODAY: MobileTodayItem[] = [
  { time: '08:30', label: 'Révision rooftop CVC', place: 'Bastille Offices' },
  { time: '11:15', label: 'Diagnostic PAC', place: 'Clinique Valmy' },
  { time: '15:00', label: 'Contrat maintenance', place: 'Aster Labs' },
];

function clonePlain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function getActiveQuoteLines(hasProbeCheck: boolean): ActiveQuoteLine[] {
  const lines = [...QUOTE_BASE_LINES];
  if (hasProbeCheck) lines.push(QUOTE_OPTIONAL_LINE);

  return lines.map((line) => ({
    ...line,
    unitLabel: formatCurrency(line.unitCents),
    totalCents: line.unitCents * line.quantity,
    totalLabel: formatCurrency(line.unitCents * line.quantity),
  }));
}

export function getCurrentScenarioStep(flowState: FlowState): ScenarioStepId {
  if (flowState.invoiceCreated) return 'invoice';
  if (flowState.reportStatus === 'validated') return 'invoice';
  if (['submitted', 'rejected', 'resubmitted'].includes(flowState.reportStatus)) return 'report';
  if (flowState.onSite || flowState.photosCaptured || flowState.voiceCaptured || flowState.signatureCaptured) return 'mobile';
  if (flowState.quoteConverted) return 'dispatch';
  return 'quote';
}

export function getScenarioCaption(step: ScenarioStepId): string {
  const captions: Record<ScenarioStepId, string> = {
    quote: 'Le devis IA prépare le dossier Clinique Valmy avec garde-fous de marge et contexte site.',
    dispatch: 'Le devis converti crée un OT prêt à être affecté au bon technicien.',
    mobile: 'Le technicien capture preuves, vocal et signature, même hors ligne.',
    report: 'Le rapport remonte à l’exploitation avec une file de validation explicite.',
    invoice: 'La facture est prête puis générée, sans casser la trace terrain.',
  };

  return captions[step];
}

function getScenarioDispatchAssignment(state: DemoPersistedState): DispatchAssignment | null {
  if (!state.flowState.quoteConverted) return null;
  return (
    state.dispatchAssignment ?? {
      ownerName: 'Nadia L.',
      scheduledWindow: '11:15 - 12:30',
    }
  );
}

function getDispatchLanes(state: DemoPersistedState): TechnicianLane[] {
  const assignment = getScenarioDispatchAssignment(state);
  const runtimeStatus = state.flowState.onSite ? 'Sur site' : state.flowState.quoteConverted ? 'Confirmé' : 'A venir';
  const sourceJob =
    TECHNICIAN_LANES.flatMap((lane) => lane.jobs).find((job) => job.client === 'Clinique Valmy') ?? {
      time: '11:15',
      title: 'Diagnostic pompe à chaleur',
      client: 'Clinique Valmy',
      status: 'A venir',
    };

  return clonePlain(TECHNICIAN_LANES).map((lane) => {
    const remainingJobs = lane.jobs.filter((job) => job.client !== 'Clinique Valmy');
    if (!assignment) {
      return {
        ...lane,
        jobs: lane.jobs.map((job) => {
          if (job.client !== 'Clinique Valmy') return job;
          return { ...job, status: runtimeStatus };
        }),
      };
    }

    if (lane.name !== assignment.ownerName) {
      return {
        ...lane,
        jobs: remainingJobs,
      };
    }

    const scheduledStart = assignment.scheduledWindow.split(' - ')[0] ?? sourceJob.time;
    const nextJobs = [
      ...remainingJobs,
      {
        ...sourceJob,
        time: scheduledStart,
        title: 'Diagnostic pompe à chaleur',
        status: runtimeStatus,
      },
    ].sort((left, right) => left.time.localeCompare(right.time));

    return {
      ...lane,
      jobs: nextJobs,
    };
  });
}

function getWorkOrderRuntime(item: WorkOrder, state: DemoPersistedState): WorkOrder {
  const runtime = clonePlain(item);
  const flowState = state.flowState;

  if (item.id !== scenarioFocusOrderId) return runtime;

  if (flowState.quoteConverted) {
    const assignment = getScenarioDispatchAssignment(state);
    if (assignment) {
      runtime.owner = assignment.ownerName;
      runtime.window = assignment.scheduledWindow;
      runtime.nextAction = `Départ ${assignment.ownerName} puis exécution terrain`;
      runtime.timeline.push({ at: '08:12', label: `Affectation confirmée · ${assignment.ownerName} · ${assignment.scheduledWindow}` });
    }
    runtime.status = 'Affectation confirmée';
    runtime.timeline.push({ at: '08:14', label: "Intervention créée depuis le devis IA" });
  }
  if (flowState.onSite) {
    runtime.status = 'Sur site';
    runtime.timeline.push({ at: '11:17', label: 'Intervention démarrée sur mobile' });
  }
  if (flowState.photosCaptured) {
    runtime.proof.push('2 photos PAC');
    runtime.timeline.push({ at: '11:24', label: 'Photos PAC capturées hors ligne' });
  }
  if (flowState.voiceCaptured) {
    runtime.voiceDraft =
      'Diagnostic PAC confirmé. Nettoyage condenseur effectué. Contrôle sonde extérieure recommandé avant remise en service complète.';
    runtime.timeline.push({ at: '11:38', label: 'Rapport vocal structuré localement' });
  }
  if (flowState.signatureCaptured) {
    runtime.proof.push('Signature client');
    runtime.timeline.push({ at: '11:47', label: 'Signature client capturée' });
  }
  if (flowState.syncStatus === 'conflict') {
    runtime.timeline.push({ at: '11:54', label: 'Conflit de synchronisation détecté sur la signature client' });
  }
  if (flowState.reportStatus === 'submitted') {
    runtime.status = 'En validation';
    runtime.invoiceStatus = 'Validation ops';
    runtime.nextAction = 'Valider le rapport puis créer la facture';
    runtime.timeline.push({ at: '11:56', label: 'Rapport soumis depuis le mobile' });
  }
  if (flowState.reportStatus === 'rejected') {
    runtime.status = 'Reprise requise';
    runtime.invoiceStatus = 'Validation refusée';
    runtime.nextAction = flowState.rejectionReason ?? reportRejectReason;
    runtime.timeline.push({ at: '12:01', label: 'Rapport refusé par l’exploitation' });
  }
  if (flowState.reportStatus === 'resubmitted') {
    runtime.status = 'Re-soumis';
    runtime.invoiceStatus = 'Relecture ops';
    runtime.nextAction = 'Revalider après correction';
    runtime.timeline.push({ at: '12:09', label: 'Rapport corrigé et re-soumis' });
  }
  if (flowState.reportStatus === 'validated') {
    runtime.status = 'Prêt à facturer';
    runtime.invoiceStatus = 'Facture prête';
    runtime.nextAction = 'Créer la facture et envoyer le récapitulatif';
    runtime.timeline.push({ at: '12:04', label: 'Rapport validé par l’exploitation' });
  }
  if (flowState.invoiceCreated) {
    runtime.status = 'Facturé';
    runtime.invoiceStatus = 'FST-2026-0312';
    runtime.nextAction = 'Proposer le contrat maintenance PAC critique';
    runtime.timeline.push({ at: '12:07', label: 'Facture générée automatiquement' });
  }

  runtime.proof = [...new Set(runtime.proof)];
  runtime.timeline.sort((left, right) => left.at.localeCompare(right.at));
  return runtime;
}

export function getMobileSyncItems(flowState: FlowState): SyncItem[] {
  if (flowState.syncStatus === 'retrying') {
    return [
      {
        title: 'Relance de synchronisation',
        detail: 'Consolidation des preuves terrain avec le serveur',
        meta: 'En cours',
        status: 'warning',
      },
    ];
  }

  if (flowState.syncStatus === 'conflict') {
    return [
      {
        title: 'Conflit de synchronisation',
        detail: flowState.syncConflictReason ?? syncConflictReason,
        meta: 'Action requise',
        status: 'conflict',
      },
      {
        title: 'Brouillon vocal local',
        detail: 'Version terrain conservée en attente de relance',
        meta: 'Prioritaire',
        status: 'neutral',
      },
    ];
  }

  if (flowState.syncStatus === 'synced') {
    return [
      {
        title: 'Synchronisation terrain alignée',
        detail: 'Photos, signature et vocal consolidés côté serveur',
        meta: ['submitted', 'resubmitted'].includes(flowState.reportStatus) ? 'Envoyé à l’exploitation' : 'Prêt à envoyer',
        status: 'success',
      },
      ...(flowState.reportStatus === 'rejected'
        ? [
            {
              title: 'Reprise demandée',
              detail: flowState.rejectionReason ?? reportRejectReason,
              meta: 'Retour ops',
              status: 'warning' as const,
            },
          ]
        : []),
    ];
  }

  return [
    {
      title: '2 photos compressées',
      detail: 'PAC Clinique Valmy, upload différé',
      meta: 'Dernier essai 11:42',
      status: 'neutral',
    },
    {
      title: '1 brouillon vocal structuré',
      detail: 'Transcription locale prête à pousser',
      meta: 'File prioritaire',
      status: 'neutral',
    },
    {
      title: 'Signature client Clinique Valmy',
      detail: 'Checksum OK, upload différé',
      meta: 'Bloque la facture',
      status: 'warning',
    },
  ];
}

export function getBillingQueue(runtime: WorkOrder): BillingItem[] {
  return [
    {
      title: `${runtime.id} · ${runtime.client}`,
      detail: runtime.nextAction,
      amount: runtime.revenue,
      status: runtime.invoiceStatus,
      tone:
        runtime.invoiceStatus === 'Validation refusée'
          ? 'blocked'
          : runtime.invoiceStatus === 'FST-2026-0312'
            ? 'ready'
            : runtime.invoiceStatus === 'Relecture ops'
              ? 'warning'
              : undefined,
    },
    {
      title: 'WO-1819 · DataHub 17',
      detail: 'Rapport vocal à approuver avant émission',
      amount: '980 € HT',
      status: 'Validation ops',
    },
    {
      title: 'WO-1842 · Bastille Offices',
      detail: "Facture maintenance prête le jour même",
      amount: '1 240 € HT',
      status: "Prêt aujourd'hui",
      tone: 'ready',
    },
  ];
}

export function buildDemoSnapshot(state: DemoPersistedState): DemoSnapshot {
  const activeQuoteLines = getActiveQuoteLines(state.quoteHasProbeCheck);
  const subtotalCents = activeQuoteLines.reduce((sum, line) => sum + line.totalCents, 0);
  const vatCents = Math.round(subtotalCents * 0.2);
  const totalCents = subtotalCents + vatCents;
  const currentScenarioStep = getCurrentScenarioStep(state.flowState);

  return {
    kpiCards: clonePlain(KPI_CARDS),
    controlTower: clonePlain(CONTROL_TOWER),
    overviewModules: clonePlain(OVERVIEW_MODULES),
    cashBlockers: clonePlain(CASH_BLOCKERS),
    dispatchSignals: clonePlain(DISPATCH_SIGNALS),
    technicianLanes: getDispatchLanes(state),
    unassignedJobs: clonePlain(UNASSIGNED_JOBS),
    workOrders: WORK_ORDERS.map((item) => getWorkOrderRuntime(item, state)),
    quoteSignals: clonePlain(QUOTE_SIGNALS),
    activeQuoteLines,
    subtotalCents,
    vatCents,
    totalCents,
    mobileToday: clonePlain(MOBILE_TODAY),
    mobileSyncItems: getMobileSyncItems(state.flowState),
    flowState: clonePlain(state.flowState),
    quoteHasProbeCheck: state.quoteHasProbeCheck,
    dispatchAssignment: state.dispatchAssignment ? clonePlain(state.dispatchAssignment) : null,
    currentScenarioStep,
    currentScenarioCaption: getScenarioCaption(currentScenarioStep),
  };
}
